import React, {useEffect, useMemo, useState} from 'react'
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    Tooltip,
    Typography
} from '@mui/material'
import {useLocation, useNavigate} from 'react-router-dom'
import {useAuth} from '../../auth/auth'
import {RouteSecurity} from '../../common/RouteSecurity'
import {ActiveIteration, CountResult, ITER_NAMES, searchCounts} from '../utils'
import {webEvents} from '../../common/WebEvents'
import {getCachedProducts} from '../productCache'
import {API} from '../../common/axios'
import CheckIcon from '@mui/icons-material/Check'
import {DashboardLinkCard} from '../../common/ui'
import {createError} from '../../store/slices/error'
import {useDispatch} from 'react-redux'


function CountsDashboard() {
    const {getPermissions, getTruck} = useAuth()
    let routeSecurity = new RouteSecurity(getPermissions())
    let truckName = getTruck()
    const location = useLocation()
    const navigate = useNavigate()
    const [activeIter, setActiveIter] = useState<ActiveIteration | null>(null)
    const [alertOpen, setAlertOpen] = useState(false)
    const [counts, setCounts] = useState<CountResult[]>([])
    const [inProgressCounts, setInProgressCounts] = useState<number>(0)
    const [pendingCounts, setPendingCounts] = useState<number>(0)
    const [inReviewCounts, setInReviewCounts] = useState<number>(0)
    const [open, setOpen] = useState(false)
    const [beginCountDialog, setBeginCountDialog] = useState(false)
    const dispatch = useDispatch()

    useEffect(() => {
        const submissionSuccessItem = window.sessionStorage.getItem('submissionSuccess')
        if (submissionSuccessItem === 'true') {
            setAlertOpen(true)
            setActiveIter(null)
            window.sessionStorage.setItem('submissionSuccess', 'false')
            return
        }
        let isMounted = true
        let activeIter: ActiveIteration | null = null

        searchCounts({
            location_ids: truckName,
            iteration_statuses: 'pending,in-progress'
        })
            .then((counts: CountResult[]) => {
                if (!isMounted) return

                if (counts.length) {
                    let c = counts[0]
                    let iter = c.iteration

                    activeIter = {
                        countId: c.countId,
                        iterationId: iter.iterationId,
                        type: c.type,
                        status: iter.status,
                        sample: iter.sample,
                        description: c.description,
                        assignedAt: iter.assignedAt,
                        requestedBy: iter.requestedBy,
                        requestedAt: iter.requestedAt
                    }
                    window.sessionStorage.setItem('activeIter', JSON.stringify(activeIter))
                    window.sessionStorage.setItem('submissionSuccess', 'false')
                    setActiveIter(activeIter)
                }
            })
            .catch(error => {
                if (isMounted) {
                    webEvents.error(
                        error.message,
                        {iteration: activeIter},
                        error.stack
                    )
                    const causeMessage = error?.cause?.messages?.[0]
                        || error?.messages?.[0]
                        || 'Unknown error'

                    dispatch(createError({
                        type: error.type,
                        message: causeMessage
                    }))
                }
            })

        getCachedProducts()
            .then()
            .catch(error => {
                if (isMounted) {
                    webEvents.error(
                        error.message,
                        {iteration: activeIter},
                        error.stack
                    )
                    const causeMessage = error?.cause?.messages?.[0]
                        || error?.messages?.[0]
                        || 'Unknown error'

                    dispatch(createError({
                        type: error.type,
                        message: causeMessage
                    }))
                }
            })

        const loadCounts = async () => {
            try {
                const data = await searchCounts({
                    iteration_statuses: 'pending,in-progress,in-review',
                    page_size: '200'
                })

                setCounts(data)
                setInProgressCounts(data.filter(count => count.iteration.status === 'in-progress').length)
                setPendingCounts(data.filter(count => count.iteration.status === 'pending').length)
                setInReviewCounts(data.filter(count => count.iteration.status === 'in-review').length)
            } catch (error) {
                console.error('Failed to fetch counts', error)
            }
        }

        loadCounts()

        return () => {
            isMounted = false
            window.sessionStorage.setItem('submissionSuccess', 'false')
        }
    }, [location.state])

    let action =
        activeIter?.status === 'pending' ? 'Begin Count' : 'Resume Count'

    const IterationDetails = useMemo(() => {
        if (!activeIter) return null

        return (
            <Box
                display="flex"
                flexDirection="column"
                alignItems="flex-start"
                color="text.secondary"
                textOverflow="ellipsis"
                width="100%">
                <Typography variant="h6" component="div">
                    {activeIter.type[0].toUpperCase() +
                        activeIter.type.slice(1)}{' '}
                    Count {activeIter.countId}
                    <Typography component="span" variant="body2">
                        {' '}
                        — {ITER_NAMES[activeIter.iterationId]} Iteration
                    </Typography>
                </Typography>
                <TableContainer component={Paper} sx={{maxWidth: '100%'}}>
                    <Table size="small" sx={{width: '100%', maxWidth: '100%'}}>
                        <TableBody sx={{maxWidth: '100%'}}>
                            <TableRow>
                                <TableCell>Status</TableCell>
                                <TableCell>{activeIter.status}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell>Requester</TableCell>
                                <TableCell>
                                    <Typography sx={{ fontSize: '0.875rem' }}>{activeIter.requestedBy}</Typography>
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell>Assigned</TableCell>
                                <TableCell>
                                    {activeIter.assignedAt?.toLocaleString() ?? (
                                        <Typography
                                            variant="body2"
                                            color="textSecondary">
                                            Unassigned
                                        </Typography>
                                    )}
                                </TableCell>
                            </TableRow>
                            <TableRow sx={{'&:hover': {backgroundColor: 'whitesmoke'}}}>
                                <TableCell>Sample</TableCell>
                                {activeIter?.sample ? (
                                    <Tooltip title={<h1 style={{
                                        color: 'text.primary',
                                        fontSize: 15,
                                        fontWeight: 'medium'
                                    }}>Sample items:<br />
                                        {activeIter.sample
                                            .map(x => x.sku)
                                            .join(', ')}
                                    </h1>} onClick={(e) => {
                                        e.stopPropagation()
                                        handleClickOpen()
                                    }}>
                                        <TableCell>
                                            {activeIter.sample.length} items
                                        </TableCell>
                                    </Tooltip>
                                ) : (
                                    <TableCell>
                                        ALL BINS
                                    </TableCell>
                                )}
                            </TableRow>
                            <TableRow>
                                <TableCell>Description</TableCell>
                                <TableCell>
                                    {activeIter.description ?? ''}
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        )
    }, [activeIter])

    function startCounting() {
        if (activeIter === null) return

        let path = activeIter.sample ? '/inventory/counts/sample' : '/inventory/counts/bin'

        /* assign the count iteration when it is pending */
        if (activeIter.status === 'pending') {
            API.post(`inventory/counts/${activeIter.countId}/assign`)
                .then(r => {
                    let iter = {...activeIter}
                    iter.assignedAt = r.data.iteration.assigned_at
                    iter.status = r.data.iteration.status
                    window.sessionStorage.setItem('activeIter', JSON.stringify(iter))
                    window.sessionStorage.setItem('submissionSuccess', 'false')
                    navigate(path)
                })
                .catch(error => {
                    webEvents.error(
                        error.message,
                        {iteration: activeIter},
                        error.stack
                    )
                    const causeMessage = error?.cause?.messages?.[0]
                        || error?.messages?.[0]
                        || 'Unknown error'

                    dispatch(createError({
                        type: error.type,
                        message: causeMessage
                    }))
                })
        } else {
            window.sessionStorage.setItem('submissionSuccess', 'false')
            navigate(path)
        }
    }

    const handleClickOpen = () => {
        setOpen(true)
    }

    const handleClose = () => {
        setOpen(false)
    }

    const confirmBeginCount = () => {
        setBeginCountDialog(true)
    }

    const handleCloseBeginCountDialog = () => {
        setBeginCountDialog(false)
    }

    return (
        <Box display="flex" flexDirection="column" alignItems="center" p={3} gap={2}>
            {alertOpen && (
                <Alert
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        width: '80%',
                        mx: 'auto',
                        alignItems: 'flex-start',
                        my: 1
                    }}
                    icon={<CheckIcon fontSize="inherit" />} severity="success"
                    onClose={() => setAlertOpen(false)}>
                    Report successfully submitted for review!
                </Alert>
            )}
            <Typography variant="h4" gutterBottom marginTop={3}>Counts Dashboard</Typography>
            <Box display="flex" flexWrap="wrap" alignItems="center" justifyContent="center" gap={3} p={3}
                 sx={{mx: 'auto', width: 'full'}}>
                <DashboardLinkCard
                    style={{
                        width: 350,
                        height: 350
                    }}
                    title={action}
                    clickHandler={activeIter?.status === 'pending' ? confirmBeginCount : startCounting}
                    text={`${getTruck()} has ${activeIter ? 'an' : 'no'} active count`}
                >
                    <>{truckName ? activeIter ? IterationDetails : null :
                        <h3>You don&apos;t have an assigned Truck to perform a count</h3>}
                    </>
                </DashboardLinkCard>
                <DashboardLinkCard
                    style={{
                        width: 350,
                        height: 350
                    }}
                    title="Review"
                    text={`${inReviewCounts} counts waiting for review`}
                    route="/inventory/counts/review"
                />
                <DashboardLinkCard
                    style={{
                        width: 350,
                        height: 350
                    }}
                    title="Manage"
                    route="/inventory/counts/manage"
                >
                    <Typography sx={{mt: 1.5}} color="text.secondary">
                        In-Progress: {inProgressCounts} counts
                    </Typography>
                    <Typography sx={{mt: 1.5}} color="text.secondary">
                        Pending: {pendingCounts} counts
                    </Typography>
                </DashboardLinkCard>
            </Box>

            {activeIter?.sample && (
                <Dialog open={open} onClose={handleClose} sx={{margin: 5, padding: 2}}>
                    <DialogTitle sx={{borderBottom: '1px solid #ddd', paddingBottom: 2}}>
                        Sample items:
                    </DialogTitle>
                    <DialogContent sx={{padding: 3}}>
                        <h2 style={{marginTop: 10}}>
                            <span style={{fontSize: '1em'}}>
                                {activeIter.sample
                                    .map(x => x.sku)
                                    .join(', ')}</span>
                        </h2>
                    </DialogContent>
                    <DialogActions sx={{justifyContent: 'space-between', padding: 2}}>
                        <Button onClick={handleClose} color="primary">
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>

            )}

            {beginCountDialog && (
                <Dialog
                    open={beginCountDialog}
                    onClose={handleCloseBeginCountDialog}>
                    <DialogTitle>
                        Confirm Begin Count
                    </DialogTitle>
                    <DialogContent>
                        <Typography>
                            Once you begin counting, you cannot make changes to your
                            truck&apos;s inventory: i.e. receiving, invoicing, returns,
                            or transfers.
                        </Typography>
                    </DialogContent>
                    <DialogActions
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between'
                        }}>
                        <Button onClick={handleCloseBeginCountDialog} color="error">
                            Cancel
                        </Button>
                        <Button
                            onClick={startCounting}
                            color="primary">
                            Confirm & Begin Count
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </Box>
    )
}

export default CountsDashboard
